import React from "react";
import { useNavigate } from "react-router-dom";
import { useLang } from "../context/TranslationProvider";
import MY_CONFIG from "@/constants";

type IconPosition = "top" | "right" | "left";

interface MyIconButtonProps {
  link?: string;
  label?: string;
  icon?: React.ReactNode | string; // Can be React component OR image URL
  disabled?: boolean;
  selected?: boolean;
  onClick?: () => void;
  width?: number | string;
  height?: number | string;
  iconSize?: string;
  buttonStyle?: string;
  labelStyle?: string;
  imgClass?: string;
  iconPosition?: IconPosition; // New prop for icon position
  gap?: string; // Gap between icon and label
  fullWidth?: boolean; // Make button full width
}

const MyIconButton: React.FC<MyIconButtonProps> = ({
  link,
  label,
  icon,
  disabled = false,
  selected = false,
  onClick,
  width,
  height,
  iconSize = "w-9",
  buttonStyle = "",
  labelStyle = "text-[12px] font-medium",
  imgClass = "",
  iconPosition = "top",
  gap = "gap-2",
  fullWidth = false,
}) => {
  const { t } = useLang();
  const navigate = useNavigate();

  const handleClick = () => {
    if (disabled) return;
    if (onClick) {
      onClick();
      return;
    }
    if (link) navigate(link);
  };

  // Function to render icon (handles both React components and image URLs)
  const renderIcon = () => {
    // If icon is a string (URL)
    if (typeof icon === "string") {
      return (
        <img
          src={icon}
          alt={label || "icon"}
          className={`object-contain ${iconSize} ${imgClass}`}
          onError={(e) => {
            // Fallback to default icon on error
            (e.target as HTMLImageElement).src = MY_CONFIG.ICON_URL;
          }}
        />
      );
    }
    
    // If icon is a React node
    if (React.isValidElement(icon)) {
      return icon;
    }

    // Default fallback
    return (
      <img
        src={MY_CONFIG.ICON_URL}
        alt=""
        className={`object-contain ${iconSize}`}
      />
    );
  };

  // Determine flex direction based on icon position
  const getFlexDirection = () => {
    switch (iconPosition) {
      case "right":
        return "flex-row-reverse";
      case "left":
        return "flex-row";
      case "top":
      default:
        return "flex-col";
    }
  };

  // Determine alignment classes
  const getAlignmentClasses = () => {
    switch (iconPosition) {
      case "right":
      case "left":
        return "items-center";
      case "top":
      default:
        return "items-center";
    }
  };

  // Determine justify content for full width
  const getJustifyClass = () => {
    return fullWidth ? "justify-center" : "";
  };

  // Style object for dynamic width/height
  const style: React.CSSProperties = {};
  if (width !== undefined) style.width = typeof width === 'number' ? `${width}px` : width;
  if (height !== undefined) style.height = typeof height === 'number' ? `${height}px` : height;
  if (fullWidth) style.width = '100%';

  return (
    <button
      type="button"
      disabled={disabled}
      onClick={handleClick}
      className={`
        ${buttonStyle}
        flex ${getFlexDirection()} ${getAlignmentClasses()} ${getJustifyClass()}
        ${gap}
        rounded-md transition-all duration-200
        ${selected ? "ring-2 ring-offset-2 ring-black/40 bg-black/10" : ""}
        ${disabled ? "opacity-50 cursor-not-allowed" : "hover:scale-105 active:scale-95"}
      `}
      style={style}
    >
      {/* Icon Container */}
      {icon &&<div className={`flex items-center justify-center ${iconPosition === "top" ? "h-3/5" : ""}`}>
        {renderIcon()}
      </div>}

      {/* Label */}
      {label && (
        <div
          className={`
            ${labelStyle}
            ${iconPosition === "top" ? "mt-1" : ""}
            text-center leading-tight line-clamp-2
          `}
        >
          {t(label)}
        </div>
      )}
    </button>
  );
};

export default MyIconButton;



// EXAMPLES
// // Default (icon on top)
// <MyIconButton
//   link="/listings/new"
//   label="Browse"
//   icon="https://example.com/icon.png"
//   buttonStyle="px-6 py-3 bg-white text-purple-600 rounded-xl font-semibold hover:shadow-lg transition-all"
// />

// // Icon on left
// <MyIconButton
//   link="/listings/new"
//   label="Browse"
//   icon={<SearchIcon className="w-5 h-5" />}
//   iconPosition="left"
//   gap="gap-3"
//   buttonStyle="px-6 py-3 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700"
// />

// // Icon on right
// <MyIconButton
//   onClick={() => console.log('clicked')}
//   label="Next"
//   icon={<ChevronRightIcon className="w-5 h-5" />}
//   iconPosition="right"
//   gap="gap-2"
//   buttonStyle="px-4 py-2 bg-gray-800 text-white rounded-md hover:bg-gray-700"
// />

// // Full width button with icon on left
// <MyIconButton
//   label="Submit"
//   icon={<CheckIcon className="w-5 h-5" />}
//   iconPosition="left"
//   fullWidth={true}
//   buttonStyle="px-6 py-3 bg-green-600 text-white rounded-xl font-semibold hover:bg-green-700"
// />

// // Custom dimensions
// <MyIconButton
//   label="Profile"
//   icon="https://example.com/avatar.jpg"
//   width={120}
//   height={40}
//   iconSize="w-6 h-6"
//   iconPosition="left"
//   gap="gap-2"
//   buttonStyle="bg-purple-100 text-purple-700 rounded-full hover:bg-purple-200"
// />

// // Icon only (no label)
// <MyIconButton
//   onClick={() => console.log('settings')}
//   icon={<SettingsIcon className="w-6 h-6" />}
//   buttonStyle="p-2 bg-gray-800 rounded-full hover:bg-gray-700"
//   iconPosition="left"
// />

// // With custom gap
// <MyIconButton
//   label="Upload"
//   icon={<UploadIcon className="w-5 h-5" />}
//   iconPosition="left"
//   gap="gap-4"
//   buttonStyle="px-5 py-2.5 bg-indigo-600 text-white rounded-lg font-medium"
// />